<template>
    <div 
        class="o-schedule"
        :style="configStyles"
    >
        <div class="schedule-header">
            <div class="schedule-header-wrapper">
                <div
                    class="schedule-badge"
                    @click="getTimetables"
                >
                    <img
                        :src="icon"
                        :alt="name"
                        class="schedule-badge-image"
                    >
                </div>
                <div class="schedule-title-wrapper">
                    <span class="schedule-headline">
                        {{ $t('ui.schedules.departures') }}
                    </span>
                    <span class="schedule-title">
                        {{ name }}
                    </span>
                </div>
            </div>
            <div class="schedule-header-info">
                <span class="schedule-sync-info">
                    {{ $t('ui.schedules.last-update') }} {{ lastSyncFormatted }}
                </span>
                <span
                    v-if="distance"
                    class="schedule-distance"
                >
                    <span class="schedule-distance-highlight">
                        {{ $t('ui.popup.distance') }}
                    </span>
                    {{ distance }} m
                </span>
                <div class="schedule-floor-info">
                    <AFloorInfo
                        :text="floorInfo"
                        :view="view"
                    />
                </div>
            </div>
        </div>
        <OAviaSchedule
            v-if="isAviaSchedule"
            :schedule="schedule"
        />
        <OLandTransportSchedule
            v-else
            :schedule="schedule"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import AFloorInfo from '../../atoms/a-floor-info';
import OAviaSchedule from '../../unique-organisms/o-avia-schedule';
import OLandTransportSchedule from '../../unique-organisms/o-land-transport-schedule';

import { TRANSPORT_API_MAP } from "@/utils/services/transport";
import { PLACEHOLDERS } from "@/components/unique-organisms/o-schedule/constants";
import { convertDataStructureToCommon } from "../../../utils/services/resrobot-service";
import { TRANSPORT_TYPES } from "./constants";

export default {
    name: "OSchedule",
    components: {
        AFloorInfo,
        OAviaSchedule,
        OLandTransportSchedule
    },
    props: {
        icon: {
            type: String,
            default: ''
        },
        name: {
            type: String,
            default: ''
        },
        service: {
            type: Object,
            required: true
        },
        minPlaceholders: {
            type: Boolean,
            default: false
        },
        view: {
            type: String,
            default: ''
        },
        transportType: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            schedule: [],
            lastSync: ''
        }
    },
    computed: {
        ...mapGetters([
            't',
            'getMapConfigProp'
        ]),
        distance() {
            return this.t(this.service, 'distance') || '';
        },
        ruterStopId() {
            return this.t(this.service, 'ruter_stop_id') || null;
        },
        serviceName() {
            return this.t(this.service, 'name') || '';
        },
        apiProvider() {
            return this.t(this.service, 'timetables_api_provider') || null;
        },
        transportTypes() {
            return this.t(this.service, 'ruter_transport_types') || null;
        },
        lastSyncFormatted() {
            return this.$moment(this.lastSync).format("YYYY-MM-DD H:mm:ss");
        },
        floorInfo() {
            return this.t(this.service, 'floor') || '';
        },
        isAviaSchedule() {
            return this.transportType === TRANSPORT_TYPES.PLANE;
        },
        commonConfigStyles() {
            return this.getMapConfigProp('components.OSchedule.styles') || {};
        },
        viewConfigStyles() {
            return this.getMapConfigProp(`views.${this.view}.mainFrame.OSchedule.styles`) || {};
        },
        configStyles() {
            return {
                ...this.commonConfigStyles,
                ...this.viewConfigStyles
            }
        },
    },
    watch: {
        service: {
            handler() {
                this.getTimetables();
            },
            immediate: true
        }
    },
    methods: {
        formatNearestTime(time) {
            const t = this.$moment(time);
            return (t > this.$moment().add(1, "hour"))
                ? t.format("H:mm")
                : Math.round(this.$moment.duration(t.diff(this.$moment())).asMinutes()) + " min";
        },
        async getTimetables() {
            if(!this.ruterStopId) throw new Error(`Station code is not set for: ${this.serviceName}`);
            if(!this.apiProvider) throw new Error(`API provider is not set for: ${this.serviceName}`);

            if(this.apiProvider && TRANSPORT_API_MAP[this.apiProvider]){
                this.schedule = this.minPlaceholders ? PLACEHOLDERS.slice(0, 2) : PLACEHOLDERS;
                try{
                    if (this.apiProvider === 'resrobot') {
                        const { Departure } = await TRANSPORT_API_MAP[this.apiProvider](this.ruterStopId)
                        this.prepareCommonDataStructure(Departure);
                    } else {
                        this.schedule = await TRANSPORT_API_MAP[this.apiProvider](this.ruterStopId, this.transportTypes)
                    }
                    this.lastSync = this.$moment()
                }
                catch(error){
                    throw new Error(`${this.apiProvider} data is not available: ${error}` )
                }
            }
        },
        prepareCommonDataStructure(data) {
            this.schedule = convertDataStructureToCommon(data, this.transportTypes)
        }
    }
}
</script>

<style lang="scss" src="./theme.scss" />
